import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import "./CheckoutPage.css";
import HeaderBanner from "../../Components/HeaderBanner/HeaderBanner";
import { useCurrency } from "../../Context/CurrencyContext";
import DeliveryMethodSelector from "../../Components/DeliveryMethodSelector/DeliveryMethodSelector";
import { openWhatsAppChat } from "../../Utilities/whatsapp";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { currency, getPrice } = useCurrency();

  const item = location.state?.item;
  const size = location.state?.size;

  // Default: GHS visitors -> momo, everyone else -> bank.
  // Once the person manually picks a tab, we stop auto-switching it,
  // even if the detected currency changes after that (e.g. geo lookup
  // resolves late).
  const [paymentMethod, setPaymentMethod] = useState(
    currency === "GHS" ? "momo" : "bank"
  );
  const [userSelectedMethod, setUserSelectedMethod] = useState(false);

  useEffect(() => {
    if (!userSelectedMethod) {
      setPaymentMethod(currency === "GHS" ? "momo" : "bank");
    }
  }, [currency, userSelectedMethod]);

  const handleSelectPaymentMethod = (method) => {
    setPaymentMethod(method);
    setUserSelectedMethod(true);
  };

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    country: "",
    address: "",
    // Momo fields
    momoProviderName: "",
    momoNumber: "",
    momoAccountName: "",
    // Bank fields
    bankName: "",
    bankAccountName: "",
    accountNumber: "",
    bic: "",
    iban: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [deliveryMethods, setDeliveryMethods] = useState({
    email: true,
    whatsapp: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // If someone goes to /checkout without an item (e.g. direct link), redirect them.
    if (!item) {
      navigate("/shop");
    }
  }, [item, navigate]);

  if (!item) return null;

  const displayImage = item.images?.[0] || item.imageUrl;

  // The amount to pay always reflects the visitor's local currency —
  // it does NOT switch based on which payment method tab is selected.
  const displayPrice = getPrice(item);

  // The currency the funds actually settle in once sent, purely for the
  // reference note below (Momo settles in GHS, Bank settles in EUR).
  const settlementCurrency = paymentMethod === "momo" ? "GHS" : "EUR";
  const showCurrencyNote = currency !== settlementCurrency;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleCopy = (text, fieldKey) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(fieldKey);
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch((err) => console.error("Copy failed:", err));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const paymentDetails =
      paymentMethod === "momo"
        ? {
            "Payment Method": "Mobile Money (GHS)",
            "Momo Provider Name": formData.momoProviderName,
            "Momo Phone Number": formData.momoNumber,
            "Momo Account Name": formData.momoAccountName,
          }
        : {
            "Payment Method": "Bank Transfer (EUR)",
            "Bank Name": formData.bankName,
            "Bank Account Name": formData.bankAccountName,
            "Account Number": formData.accountNumber || "N/A",
            BIC: formData.bic,
            IBAN: formData.iban,
          };

    const payload = {
      _subject: `New Order from ${formData.fullName}`,
      "Full Name": formData.fullName,
      Email: formData.email,
      "Phone Number": formData.phoneNumber,
      Country: formData.country,
      Address: formData.address,
      ...paymentDetails,
      "Item Purchased": item.name,
      Size: size || "N/A",
      "Displayed Currency": currency,
      "Amount To Pay": displayPrice,
      "Settlement Currency": settlementCurrency,
    };

    if (deliveryMethods.whatsapp) {
      const whatsappText = ["New Order"]
        .concat(
          Object.entries(payload)
            .filter(([key]) => key !== "_subject")
            .map(([key, value]) => `${key}: ${value}`)
        )
        .join("\n");
      openWhatsAppChat(whatsappText);
    }

    if (!deliveryMethods.email) {
      alert(
        "WhatsApp opened — press Send in WhatsApp to finish placing your order."
      );
      setIsSubmitting(false);
      navigate("/");
      return;
    }

    fetch("https://formsubmit.co/ajax/zakarry540@gmail.com", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(() => {
        alert("Order Placed Successfully! We will contact you soon.");
        navigate("/");
      })
      .catch((error) => {
        console.error(error);
        alert("Failed to submit order. Please try again.");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="checkout-page-wrapper">
      <HeaderBanner title="Checkout" currentPath="Checkout" />

      <div className="checkout-container">
        <div className="checkout-form-section">
          <h2>Billing & Shipping Details</h2>
          <form className="checkout-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  placeholder="+233 20 123 4567"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <input
                  type="text"
                  id="country"
                  placeholder="Ghana"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Location Address *</label>
              <input
                type="text"
                id="address"
                placeholder="123 Main Street, Accra"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            <h3 className="section-subtitle">Payment Details</h3>

            <div className="payment-method-tabs">
              <button
                type="button"
                className={`payment-tab ${paymentMethod === "momo" ? "active" : ""}`}
                onClick={() => handleSelectPaymentMethod("momo")}
              >
                Momo (GHS)
              </button>
              <button
                type="button"
                className={`payment-tab ${paymentMethod === "bank" ? "active" : ""}`}
                onClick={() => handleSelectPaymentMethod("bank")}
              >
                Bank (EUR)
              </button>
            </div>

            {paymentMethod === "momo" ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="momoProviderName">
                      Momo Provider Name *
                    </label>
                    <input
                      type="text"
                      id="momoProviderName"
                      placeholder="e.g. MTN Mobile Money"
                      value={formData.momoProviderName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="momoNumber">Momo Phone Number *</label>
                    <input
                      type="tel"
                      id="momoNumber"
                      placeholder="054XXXXXXX"
                      value={formData.momoNumber}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="momoAccountName">Account Name *</label>
                  <input
                    type="text"
                    id="momoAccountName"
                    placeholder="John Doe"
                    value={formData.momoAccountName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bankName">Bank Name *</label>
                    <input
                      type="text"
                      id="bankName"
                      placeholder="e.g. Revolut Bank UAB"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bankAccountName">Account Name *</label>
                    <input
                      type="text"
                      id="bankAccountName"
                      placeholder="John Doe"
                      value={formData.bankAccountName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="accountNumber">
                      Account Number (optional)
                    </label>
                    <input
                      type="text"
                      id="accountNumber"
                      placeholder="Optional"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bic">BIC *</label>
                    <input
                      type="text"
                      id="bic"
                      placeholder="e.g. REVOFRP2"
                      value={formData.bic}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="iban">IBAN *</label>
                  <input
                    type="text"
                    id="iban"
                    placeholder="FR76XXXXXXXXXXXXXXXXXXXX"
                    value={formData.iban}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            )}

            <DeliveryMethodSelector
              methods={deliveryMethods}
              onChange={setDeliveryMethods}
            />

            <button
              type="submit"
              className="place-order-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Order"}
            </button>
          </form>
        </div>

        <div className="checkout-summary-section">
          <h2>Order Summary</h2>
          <div className="order-item-card">
            <div className="order-item-img">
              <img src={displayImage} alt={item.name} loading="lazy" decoding="async" />
            </div>
            <div className="order-item-details">
              <h4>{item.name}</h4>
              {size && (
                <p className="order-item-size">
                  Size: <strong>{size}</strong>
                </p>
              )}
              <p>{item.description}</p>
            </div>
          </div>

          <div className="order-totals">
             <div className="total-row">
              <span>Subtotal</span>
              <span>{displayPrice}</span>
            </div>
            <div className="total-row grand-total">
              <span>Amount to Pay</span>
              <span>{displayPrice}</span>
            </div>
          </div>

          {showCurrencyNote && (
            <p className="currency-note">
              You've selected {paymentMethod === "momo" ? "Mobile Money" : "Bank Transfer"},
              which settles in {settlementCurrency}. If your bank/provider charges a
              conversion fee from {currency}, the final amount debited may differ
              slightly from what's shown above.
            </p>
          )}

          <div className="payment-instructions">
            <h3>Payment Instructions</h3>
            <p>
              Please make the payment to the following{" "}
              {paymentMethod === "momo" ? "Mobile Money number" : "bank account"}{" "}
              to complete your order.
            </p>
            {paymentMethod === "momo" ? (
              <div className="seller-bank-details">
                <div className="detail-row">
                  <p>
                    <strong>Momo Number:</strong> +233 20 335 3727
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy("+233 20 335 3727", "momoNumberDetail")}
                  >
                    {copiedField === "momoNumberDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="detail-row">
                  <p>
                    <strong>Account Name:</strong> Fati Zakari Cobbinah
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy("Fati Zakari Cobbinah", "momoAccountNameDetail")}
                  >
                    {copiedField === "momoAccountNameDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="seller-bank-details">
                <div className="detail-row">
                  <p>
                    <strong>Account Name:</strong> Rabiatu Zakari
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy("Rabiatu Zakari", "bankAccountNameDetail")}
                  >
                    {copiedField === "bankAccountNameDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="detail-row">
                  <p>
                    <strong>Bank Name:</strong> Revolut Bank UAB, France
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy("Revolut Bank UAB, France", "bankNameDetail")}
                  >
                    {copiedField === "bankNameDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="detail-row">
                  <p>
                    <strong>IBAN:</strong> FR7628233000013048150495982
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() =>
                      handleCopy("FR7628233000013048150495982", "ibanDetail")
                    }
                  >
                    {copiedField === "ibanDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="detail-row">
                  <p>
                    <strong>BIC:</strong> REVOFRP2
                  </p>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopy("REVOFRP2", "bicDetail")}
                  >
                    {copiedField === "bicDetail" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}